import type {
  GetCustomerPortalDailyUsageDataResponsePayload,
  GetCustomerPortalMonthlyUsageDataResponsePayload,
} from '@vy/api-definition';
import { getOptionalObjectProperty, isEmptyArray, isNotEmptyArray, uniqueArray, vyDate } from '@vy/utils';
import { useEffect, useMemo, useState } from 'react';
import type { CustomerPortalChartSeries } from '../CustomerPortalPage/customer-portal-components/CustomerPortalChart.tsx/CustomerPortalChart';
import { setColorFromPalette } from '../CustomerPortalPage/customer-portal-components/CustomerPortalChart.tsx/CustomerPortalUtils';
import {
  buildDataForChart,
  CUSTOMER_PORTAL_CHART_CONFIGS,
  MAX_ROWS_IN_CHART,
  type ChartDataKey,
  type UsageDataItem,
} from './use-customer-portal-chart-data-utils';
type UseCustomerPortalChartDataParams = {
  data: UsageDataItem[] | undefined;
  dateFormatter: (date: Date) => string;
  dateKey: ChartDataKey;
};

function useCustomerPortalChartData({
  data,
  dateFormatter,
  dateKey,
}: UseCustomerPortalChartDataParams) {
  const chartData = useMemo(() => {
    if (isEmptyArray(data)) {
      return [];
    }
    return buildDataForChart(data, dateFormatter, dateKey);
  }, [data, dateFormatter, dateKey]);

  const chartSeries = useMemo<CustomerPortalChartSeries[]>(() => {
    if (isEmptyArray(data)) {
      return [];
    }

    const products = uniqueArray(data.map((item) => item.productName));

    return products.map((product, index) => ({
      type: 'bar' as const,
      dataKey: product,
      label: product,
      color: setColorFromPalette(index),
      stack: 'usage',
    }));
  }, [data]);

  return { chartData, chartSeries };
}

function useCustomerPortalBaseChart({
  data,
  config,
}: {
  data: { totalValue: number; date: Date; productName: string }[] | undefined;
  config: typeof CUSTOMER_PORTAL_CHART_CONFIGS.MONTHLY;
}) {
  const { chartData, chartSeries } = useCustomerPortalChartData({
    data,
    dateFormatter: config.dateFormatter,
    dateKey: config.dateKey,
  });

  return {
    chartData,
    chartSeries,
    xAxisValueFormatter: config.xAxisValueFormatter,
    tooltipTitle: config.tooltipTitle,
  };
}

export function useCustomerPortalMonthlyUsageChart({
  data,
}: {
  data: GetCustomerPortalMonthlyUsageDataResponsePayload | undefined;
}) {
  return useCustomerPortalBaseChart({
    data,
    config: CUSTOMER_PORTAL_CHART_CONFIGS.MONTHLY,
  });
}

export function useCustomerPortalDailyUsageChart(
  dailyUsageData: GetCustomerPortalDailyUsageDataResponsePayload | undefined,
) {
  const availableMonths = useMemo(() => {
    if (isEmptyArray(dailyUsageData)) {
      return [];
    }

    const months = uniqueArray(dailyUsageData.map((item) => vyDate(item.date).format('YYYY-MM')));
    const sortedMonths = months.sort();

    return sortedMonths.map((month) => ({
      value: month,
      label: vyDate(month + '-01').format('MMM YYYY'),
    }));
  }, [dailyUsageData]);

  const shouldShowMonthSelector = useMemo(() => {
    if (isEmptyArray(dailyUsageData)) {
      return false;
    }

    const uniqueDates = uniqueArray(dailyUsageData.map((item) => vyDate(item.date).format('YYYY-MM-DD')));
    return isNotEmptyArray(uniqueDates) && uniqueDates.length > MAX_ROWS_IN_CHART;
  }, [dailyUsageData]);

  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  useEffect(() => {
    if (shouldShowMonthSelector && isNotEmptyArray(availableMonths) && !selectedMonth) {
      const lastAvailableMonth = availableMonths[availableMonths.length - 1];
      const lastMonthValue = getOptionalObjectProperty(lastAvailableMonth, 'value');
      setSelectedMonth(lastMonthValue ?? null);
    }
  }, [shouldShowMonthSelector, availableMonths, selectedMonth]);

  const filteredData = useMemo(() => {
    if (isEmptyArray(dailyUsageData)) {
      return [];
    }

    if (shouldShowMonthSelector && selectedMonth) {
      return dailyUsageData.filter((item) => vyDate(item.date).format('YYYY-MM') === selectedMonth);
    }

    return dailyUsageData;
  }, [dailyUsageData, selectedMonth, shouldShowMonthSelector]);

  const baseChartData = useCustomerPortalBaseChart({
    data: filteredData,
    config: CUSTOMER_PORTAL_CHART_CONFIGS.DAILY,
  });

  return {
    ...baseChartData,
    availableMonths,
    selectedMonth,
    setSelectedMonth,
    shouldShowMonthSelector,
  };
}
